import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck, Lock, Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalystLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/analyst/login`, {
        email,
        password
      });
      
      // Store token
      localStorage.setItem('analyst_token', response.data.access_token);
      localStorage.setItem('analyst_data', JSON.stringify(response.data.analyst));
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      toast.success('Bienvenue !');
      navigate('/analyst/dashboard');
      
    } catch (error) {
      console.error('Analyst login error:', error);
      if (error.response?.status === 401) {
        toast.error('Email ou mot de passe incorrect');
      } else {
        toast.error('Erreur de connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-transparent py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div className="h-6 w-px bg-white/20" />
          <h1 className="text-white font-heading text-xl font-bold">
            L'ŒIL DU MARCHAND
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-500/20 mb-4">
              <UserCheck className="w-10 h-10 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Espace Analyste</h2>
            <p className="text-white/60">Connectez-vous pour accéder à vos dossiers</p>
          </div>

          {/* Login Form */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardContent className="p-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-white/80">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre.email@exemple.com"
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      data-testid="analyst-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white/80">Mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Votre mot de passe"
                      className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      data-testid="analyst-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gold hover:text-gold/80 transition-colors z-10"
                      style={{ cursor: 'pointer' }}
                    >
                      {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="analyst-login-btn"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Info */}
          <p className="text-center text-white/40 text-sm mt-6">
            Vos identifiants vous ont été fournis par l'administrateur
          </p>
        </div>
      </main>
    </div>
  );
}
